import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslate } from '@repo/unicity'
import InputBox from '../forms/inputbox/InputBox'
import CountryBox from './CountryBox'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { authenticationState, cartState, countryState, customerState, languageState, periodState, productsState } from '@repo/unicity'
import SearchAddress from './SearchAddress'
import { spotlightCustomerHelpers as customerHelpers } from '../../helpers/customerCompat'
import { useEffect, useRef, useState } from 'react'
import { addressApis, type TCreateUuidData } from './Apis'
import { useNavigate } from 'react-router'
import type { TOrderDetail } from '../../types/TOrderdetail'
import ErrorBox from '../errorBox/ErrorBox'
import { formAddressFiledState, formNewAddressState } from '../store/formNewAddress'
import { orderDetailState } from '../store/orderDetail'

const AddressForm = [
    {
        label: 'full_name',
        name: 'fullName',
        type: 'text',
        placeholder: 'full_name',
        required: true
    },
    {
        label: 'zip_code',
        name: 'zip',
        type: 'text',
        placeholder: 'zip_code',
        required: true
    },
    {
        label: 'country',
        name: 'country',
        type: 'CountryBox',
        placeholder: 'country',
        required: true
    },
    {
        label: 'prefecture',
        name: 'prefecture',
        type: 'text',
        placeholder: 'prefecture',
        required: true
    },
    {
        label: 'district',
        name: 'district',
        type: 'text',
        placeholder: 'district',
        required: true
    },
    {
        label: 'house_number',
        name: 'houseNumber',
        type: 'text',
        placeholder: 'house_number',
        required: true
    },
    {
        label: 'town',
        name: 'townNumber',
        type: 'text',
        placeholder: 'town',
        required: true
    }
]

type IUuidItemCart = {
    item_code: string
    qty: number
}
const NewAddress = ({
    orderDetail,
    getAddressHistory,
    setParentError
}: {
    orderDetail: TOrderDetail
    getAddressHistory: () => Promise<void>
    parentError: string[]
    setParentError: (error: string[]) => void
}) => {
    const { translate } = useTranslate()
    const language = useAtomValue(languageState)
    const country = useAtomValue(countryState)
    const customer = useAtomValue(customerState)
    const period = useAtomValue(periodState)
    const products = useAtomValue(productsState)
    const cart = useAtomValue(cartState)
    const formRef = useRef<HTMLFormElement>(null)
    const [isDisabled, setIsDisabled] = useState(true)
    const [isLoading, setIsLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string[]>([])
    const auth = useAtomValue(authenticationState)
    const setFormNewAddress = useSetAtom(formNewAddressState)
    const [, setIsLoadingUuid] = useState(false)
    const setOrderDetail = useSetAtom(orderDetailState)
    const navigate = useNavigate()

    // Add state for address fields
    const [addressFields, setAddressFields] = useAtom(formAddressFiledState)

    const allowedAddressHistory = () => {
        return customer.mainAddress.country === country.shorter.trim()
    }

    const fetchUuid = async (uuid: string) => {
        try {
            setIsLoadingUuid(true)
            const response = await addressApis.getOrderDetail({ uuid: uuid as string })
            setOrderDetail(response.data)
            setIsLoadingUuid(false)
        } catch (error) {
            console.error(error)
            setIsLoadingUuid(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setErrorMessage([])
        const formData = new FormData(e.target as HTMLFormElement)
        const data = Object.fromEntries(formData)

        const items: IUuidItemCart[] = []
        products.forEach(product => {
            items.push({
                item_code: product.item_code,
                qty: cart.find(c => c.product.item_code === product.item_code)?.quantity as number
            })
        })

        const address1 = addressFields.houseNumber ? addressFields.houseNumber : addressFields.townNumber
        const address2 = addressFields.houseNumber
            ? `${addressFields.prefecture.trim()}${addressFields.district.trim()}${addressFields.townNumber.trim()}`
            : `${addressFields.prefecture.trim()}${addressFields.district.trim()}`
        const zip = addressFields.zip

        const shipToAddress = {
            address1: address1.trim(),
            address2: address2.trim(),
            city: '',
            country: country.shorter.trim(),
            state: '',
            zip: zip.trim()
        }

        const postData = {
            fullname_jp: data.fullName as string,
            token: auth.token as string,
            ba_status: customerHelpers.customerStatus(customer) as string,
            email: orderDetail.ushop.detail_page.email.length > 0 ? orderDetail.ushop.detail_page.email : customer?.email,
            login_id: customer?.id.unicity,
            login_name: customer?.humanName.fullName,
            login_name_native: customerHelpers.customerNameNative(customer) || '',
            period: period.entryPeriod,
            phone: orderDetail.ushop.detail_page.phone.length > 0 ? orderDetail.ushop.detail_page.phone : customerHelpers.customerPhone(customer),
            agent: 'UNISHOP-WEB-JP',
            medium: 'Internet',
            platform: 'platform',
            send_confirmation: true,
            shipment_options: 'pickuptokyo',
            shipToAddress: shipToAddress,
            items: JSON.stringify(items),
            district: addressFields.district,
            prefecture: addressFields.prefecture,
            town: addressFields.townNumber,
            house_number: addressFields.houseNumber,
            zip: addressFields.zip,
            selectAddressHistory: 'new'
        } as TCreateUuidData

        try {
            setIsLoading(true)
            const response = await addressApis.createUuid({ data: postData, isSaveAddressHistory: true })
            if (response.data.success) {
                if (allowedAddressHistory()) {
                    await getAddressHistory()
                }
                setFormNewAddress({
                    shipToAddress: shipToAddress,
                    prefecture: addressFields.prefecture,
                    district: addressFields.district,
                    town: addressFields.townNumber,
                    houseNumber: addressFields.houseNumber,
                    shipToName: {
                        fullName: data.fullName as string
                    }
                })
                await fetchUuid(response.data.uuid)
                const ss = new URLSearchParams(window.location.search).get('ss')
                const address = new URLSearchParams(window.location.search).get('address')
                navigate(`/payment?uuid=${response.data.uuid}${ss ? `&ss=${ss}` : ''}${address ? `&address=${address}` : ''}`)
            } else {
                setErrorMessage(response.data.Message_v2)
            }
        } catch (error: unknown) {
            console.error(error)
            setErrorMessage([translate('error_occurred')])
        } finally {
            setIsLoading(false)
        }
    }

    const checkDisabled = () => {
        if (!formRef.current) return true

        const formData = new FormData(formRef.current)
        const data = Object.fromEntries(formData)

        if (data.fullName === '' || data.zip === '' || data.country === '' || data.prefecture === '' || data.district === '' || data.townNumber === '') {
            setIsDisabled(true)
        } else {
            setIsDisabled(false)
        }
    }

    const handleAddressUpdate = (field: string, value: string) => {
        setAddressFields(prev => ({
            ...prev,
            [field]: value
        }))
        checkDisabled()
    }

    useEffect(() => {
        if (orderDetail.ushop.detail_page.shipToAddress?.country.length > 0 && language) {
            const qs = new URLSearchParams(window.location.search)
            if (qs.get('address') === 'new') {
                setAddressFields({
                    fullName: '',
                    zip: '',
                    prefecture: '',
                    district: '',
                    townNumber: '',
                    houseNumber: ''
                })
                checkDisabled()
            } else {
                setAddressFields({
                    fullName: orderDetail.ushop.detail_page.fullname_jp,
                    zip: orderDetail.ushop.detail_page.zip,
                    prefecture: orderDetail.ushop.detail_page.prefecture,
                    district: orderDetail.ushop.detail_page.district,
                    townNumber: orderDetail.ushop.detail_page.town,
                    houseNumber: orderDetail.ushop.detail_page.house_number
                })
                checkDisabled()
            }
        }
    }, [orderDetail, language])

    useEffect(() => {
        // check if addressFields is changed
        if (JSON.stringify(addressFields) !== JSON.stringify(orderDetail.ushop.detail_page)) {
            setTimeout(() => {
                checkDisabled()
            }, 1000)
        }
    }, [JSON.stringify(addressFields)])

    return (
        <Card
            sx={{
                '&.MuiPaper-root': {
                    padding: {
                        xs: '24px',
                        sm: '24px',
                        md: '32px'
                    }
                },
                position: 'relative'
            }}>
            {isLoading && (
                <Stack
                    position={'absolute'}
                    top={'0px'}
                    right={'0px'}
                    width={'100%'}
                    height={'100%'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    zIndex={1000}
                    bgcolor={'rgba(255, 255, 255, 0.5)'}>
                    <CircularProgress size={24} sx={{ color: '#003B6F' }} />
                </Stack>
            )}
            <CardContent>
                <Stack direction={'column'} gap={'8px'}>
                    <Typography variant="cardTitle" color={'#003B6F'}>
                        {translate('new_address')}
                    </Typography>
                </Stack>
                <Stack direction={'column'} gap={'8px'}>
                    <form ref={formRef} id="address-form" onSubmit={handleSubmit}>
                        <Stack
                            mt="24px"
                            width={{
                                xs: '100%',
                                sm: '100%',
                                md: '50%'
                            }}>
                            <SearchAddress onAddressSelect={handleAddressUpdate} />
                        </Stack>
                        <Stack
                            direction={'column'}
                            gap={'16px'}
                            mt="24px"
                            sx={{
                                '@media (min-width: 1180px)': {
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '16px'
                                }
                            }}>
                            {AddressForm.map(item => {
                                if (item.name === 'country') {
                                    return <CountryBox name={item.name} type={item.type} country={country} key={item.name} />
                                } else {
                                    return (
                                        <InputBox
                                            label={translate(item.label)}
                                            key={item.name}
                                            onKeyUp={checkDisabled}
                                            name={item.name}
                                            type={item.type}
                                            placeholder={translate(item.placeholder)}
                                            value={addressFields[item.name as keyof typeof addressFields] || ''}
                                            onChange={(value: string) => {
                                                setAddressFields(prev => ({
                                                    ...prev,
                                                    [item.name]: value
                                                }))
                                                checkDisabled()
                                            }}
                                        />
                                    )
                                }
                            })}
                        </Stack>
                        {errorMessage.length > 0 && (
                            <Stack mt="24px">
                                <ErrorBox messages={errorMessage} />
                            </Stack>
                        )}
                        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} width={'100%'} mt={'24px'}>
                            <Button
                                variant="contained"
                                color="primary"
                                sx={{ minWidth: '170px', width: '170px' }}
                                type="submit"
                                disabled={isDisabled || isLoading}
                                disableElevation>
                                {translate('save_address')}
                            </Button>
                            {(allowedAddressHistory() || new URLSearchParams(window.location.search).get('address') === 'new') && (
                                <Box
                                    onClick={() => {
                                        setParentError([])
                                        navigate(
                                            `/payment?uuid=${new URLSearchParams(window.location.search).get('uuid')}${new URLSearchParams(window.location.search).get('ss') ? `&ss=${new URLSearchParams(window.location.search).get('ss')}` : ''}`
                                        )
                                    }}
                                    sx={{ cursor: 'pointer' }}>
                                    <Typography variant="body1" color={'#003B6F'}>
                                        {translate('cancel')}
                                    </Typography>
                                </Box>
                            )}
                        </Stack>
                    </form>
                </Stack>
            </CardContent>
        </Card>
    )
}

export default NewAddress
